import { getLoginUrl, getApiBaseUrl } from "@/const";
import { initializeSessionBridge, clearSessionToken } from "@/_core/session";
import { trpc } from "@/lib/trpc";
import { TRPCClientError } from "@trpc/client";
import { useCallback, useEffect, useMemo, useState } from "react";

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
  redirectPath?: string;
};

export function useAuth(options?: UseAuthOptions) {
  const { redirectOnUnauthenticated = false, redirectPath = getLoginUrl() } =
    options ?? {};
  const utils = trpc.useUtils();

  const [isSessionReady, setIsSessionReady] = useState(false);

  // Initialize session bridge on mount
  useEffect(() => {
    initializeSessionBridge(getApiBaseUrl())
      .catch(error => {
        console.warn("[Auth] Failed to initialize session bridge:", error);
      })
      .finally(() => {
        setIsSessionReady(true);
      });
  }, []);

  const meQuery = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: isSessionReady,
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      utils.auth.me.setData(undefined, null);
    },
  });

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error: unknown) {
      if (
        error instanceof TRPCClientError &&
        error.data?.code === "UNAUTHORIZED"
      ) {
        return;
      }
      throw error;
    } finally {
      clearSessionToken();
      utils.auth.me.setData(undefined, null);
      await utils.auth.me.invalidate();
    }
  }, [logoutMutation, utils]);

  const state = useMemo(() => {
    return {
      user: meQuery.data ?? null,
      loading: meQuery.isLoading || logoutMutation.isPending,
      error: meQuery.error ?? logoutMutation.error ?? null,
      isAuthenticated: Boolean(meQuery.data),
    };
  }, [
    meQuery.data,
    meQuery.error,
    meQuery.isLoading,
    logoutMutation.error,
    logoutMutation.isPending,
  ]);

  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (meQuery.isLoading || logoutMutation.isPending) return;
    // Avoid infinite login loops on transient network/server errors.
    // `auth.me` is a public endpoint; errors here usually mean the API origin is wrong or unreachable.
    if (meQuery.error) return;
    if (state.user) return;
    if (typeof window === "undefined") return;
    // redirectPath may be an absolute URL (default) or a pathname; normalize for loop prevention.
    let redirectPathname = redirectPath;
    try {
      redirectPathname = new URL(redirectPath, window.location.origin).pathname;
    } catch {
      // keep as-is
    }
    if (window.location.pathname === redirectPathname) return;

    window.location.href = redirectPath
  }, [
    redirectOnUnauthenticated,
    redirectPath,
    logoutMutation.isPending,
    meQuery.error,
    meQuery.isLoading,
    state.user,
  ]);

  return {
    ...state,
    refresh: () => meQuery.refetch(),
    logout,
  };
}
